<script setup lang="ts" generic="T extends { syntheticId: number }">
import { reactive } from "vue";
import useVuelidate from "@vuelidate/core";

import Modal from "@/components/Modal.vue";
import ListItemWrapper from "./ListItemWrapper.vue";

export interface Props<T> {
    items: T[],
    currentItem: T | null,
    header?: string,
}

const props = defineProps<Props<T>>();

const emit = defineEmits<{
        (on:"update:item", value: T | null): void
        (on:"update:items", value: T[]): void
        (on:"save", value: T[]): void
    }>();

const state = reactive<{
        showCloseModal: boolean,
        isEdit: boolean,
        itemIdToDelete: number | null,
    }>({
        showCloseModal: false,
        isEdit: false,
        itemIdToDelete: null,
    });

const v$ = useVuelidate();

const handleSave = async () => {
    const isValid = await v$.value.$validate();

    if (!isValid) {
        return;
    }
    const currentItems = [...(props.items || [])];
    if (props.currentItem) {
        if (state.isEdit) {
            const editingIndex = currentItems
                .findIndex(read => read.syntheticId === props.currentItem?.syntheticId);
            currentItems[editingIndex] = props.currentItem;
            state.isEdit = false;
        } else {
            currentItems.push(props.currentItem);
        }
        emit("save", currentItems);
        state.showCloseModal = false;
    }
};

const handleEdit = (syntheticId: string | number) => {
    const itemToEdit = props.items.find(i => i.syntheticId === syntheticId);
    state.isEdit = true;
    if (itemToEdit) {
        emit("update:item", { ...itemToEdit });
    }
};

const handleDelete = () => {
    const itemsAfterDelete = props.items.filter(i => i.syntheticId !== state.itemIdToDelete);
    emit("update:items", [...itemsAfterDelete]);
    emit("update:item", null);
    state.itemIdToDelete = null;
};

const handleClose = () => {
    emit("update:item", null);
};

</script>
<template>
    <ul class="list-group list-group-flush">
        <li
            v-for="item in items"
            :id="`items${item.syntheticId}`"
            :key="item.syntheticId"
            class="list-group-item p-2"
        >
            <div class="row align-items-center">
                <div class="col-12 col-md-7">
                    <slot :name="item.syntheticId" />
                </div>
                <div class="col-12 col-md-5">
                    <div class="row">
                        <div class="col p-md-0 col-md-6">
                            <button
                                data-testid="edititem"
                                type="button"
                                class="btn btn-primary btn-block font-weight-bold btn-sm w-100 w-md-auto"
                                @click="handleEdit(item.syntheticId)"
                            >
                                Edit/View
                            </button>
                        </div>
                        <div class="col col-md-6">
                            <button
                                data-testid="deleteitem"
                                type="button"
                                class="btn btn-items btn-block btn-danger font-weight-bold btn-sm w-100 w-md-auto"
                                @click="state.itemIdToDelete = item.syntheticId"
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <ListItemWrapper
                v-if="props.currentItem && props.currentItem.syntheticId === item.syntheticId"
                :name="header"
                @save="handleSave"
                @close="handleClose"
            >
                <slot />
            </ListItemWrapper>
        </li>
    </ul>
    <ListItemWrapper
        v-if="currentItem && !state.isEdit"
        :name="header"
        @save="handleSave"
        @close="handleClose"
    >
        <slot />
    </ListItemWrapper>
    <Modal
        :id="`delete-item-${state.itemIdToDelete}`"
        header="Are you sure?"
        :open-focus="`close-delete-item-${state.itemIdToDelete}`"
        close-focus="save-item"
        :show="state.itemIdToDelete !== null"
        @close="state.itemIdToDelete = null"
    >
        <template #body>
            <p>Removing this will be permanent and can't be undone.</p>
        </template>
        <template #footer>
            <button
                :id="`cancelRemovingitem_${state.itemIdToDelete}`"
                type="button"
                class="btn btn-secondary"
                @click.prevent="state.itemIdToDelete = null"
            >
                Cancel
            </button>
            <button
                :id="`confirmRemovingitem_${state.itemIdToDelete}`"
                type="button"
                class="btn btn-danger font-weight-bold"
                @click.prevent="handleDelete"
            >
                Remove
            </button>
        </template>
    </Modal>
</template>
